"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { useSelector } from "react-redux";
import { useParams, usePathname } from "next/navigation";
import PropTypes from "prop-types";
import confetti from "canvas-confetti";

// Components
import CrossIcon from "@/Components/Common/Icons/CrossIcon";
import VerifyCheckIcon from "@/Components/Common/Icons/VerifyCheckIcon";
import NpiVerification from "../AdminDashboard/NpiVerification";

// Step Components (approved-product review flow)
import Agreement from "./ReviewSteps/Agreement";
import IndicationSelection from "./ReviewSteps/IndicationSelection";
import ClinicalPerformance from "./ReviewSteps/ClinicalPerformance";
import Accessibility from "./ReviewSteps/Accessibility";
import ClinicalConfidence from "./ReviewSteps/ClinicalConfidence";
import CompetitorComparison from "./ReviewSteps/CompetitorComparison";
import RealWorldInsight from "./ReviewSteps/RealWorldInsight";
import ReviewSubmitted from "./ReviewSteps/ReviewSubmitted";

// Step Components (pipeline "Share My Perspective" flow). Reuses the
// outer modal shell, header, and progress bar — only the inner step
// bodies differ. Active when the modal is mounted on /pipeline/:slug.
import PrescribeLikelihood from "./ReviewSteps/Pipeline/PrescribeLikelihood";
import PatientPopulation from "./ReviewSteps/Pipeline/PatientPopulation";
import BiggestConcern from "./ReviewSteps/Pipeline/BiggestConcern";
import InfluencingFactor from "./ReviewSteps/Pipeline/InfluencingFactor";
import RecommendToColleagues from "./ReviewSteps/Pipeline/RecommendToColleagues";
import OpenComment from "./ReviewSteps/Pipeline/OpenComment";

// RTK Query for pipeline perspective submit + prefill. Imported lazily
// (just the hooks) so the bundle for the approved-product flow stays
// unaffected.
import { useSubmitPipelinePerspectiveMutation, useGetMyPipelinePerspectiveQuery } from "@/Redux/apis/pipelinePerspective";
import { useDeleteReviewDraftMutation, useReviewDraftQuery, useSaveReviewDraftMutation } from "@/Redux/apis/dashboard";

// Constants
const PORTAL_CONTAINER_ID = "reviews-disclaimer-portal";
const CONFETTI_Z_INDEX = 100000;
const TOTAL_STEPS = 6;
const MIN_INSIGHT_LENGTH = 10;
const INITIAL_CLINICAL_RATINGS = [{
  label: "Efficacy",
  tag: "efficacy",
  description: "Real-world effectiveness for this indication",
  count: 0
}, {
  label: "Safety",
  tag: "safety",
  description: "Adverse event profile in your patients",
  count: 0
}, {
  label: "Tolerability",
  tag: "tolerability",
  description: "Side-effect management and day-to-day comfort",
  count: 0
}, {
  label: "Adherence",
  tag: "adherence",
  description: "Patient compliance and persistence over time",
  count: 0
}, {
  label: "Price/Affordability",
  tag: "priceandaffordability",
  description: "Out-of-pocket cost and coverage experience",
  count: 0
}];

// Step labels for the approved-product flow (used by `getStepInfo`).
// Pipeline flow re-purposes some step numbers but the ProgressBar
// owns its own naming/numbering map, so STEP_INFO only needs to
// describe the approved flow here. Step 9 added for the pipeline
// terminal so consumers that read STEP_INFO[9] don't get undefined.
const STEP_INFO = {
  1: {
    name: "Reviews Disclaimer",
    number: 0,
    total: TOTAL_STEPS
  },
  2: {
    name: "Select Indication",
    number: 0,
    total: TOTAL_STEPS
  },
  3: {
    name: "Clinical Performance",
    number: 1,
    total: TOTAL_STEPS
  },
  4: {
    name: "Accessibility",
    number: 2,
    total: TOTAL_STEPS
  },
  5: {
    name: "Clinical Confidence",
    number: 3,
    total: TOTAL_STEPS
  },
  6: {
    name: "Competitor Comparison",
    number: 4,
    total: TOTAL_STEPS
  },
  7: {
    name: "Real-World Insight",
    number: 5,
    total: TOTAL_STEPS
  },
  8: {
    name: "Review Submitted",
    number: 6,
    total: TOTAL_STEPS
  },
  9: {
    name: "Perspective Submitted",
    number: 7,
    total: TOTAL_STEPS
  }
};

// Custom shapes for confetti
const CONFETTI_SHAPES = {
  heart: 'M167 72c19,-38 37,-56 75,-56 42,0 76,33 76,75 0,76 -76,151 -151,227 -76,-76 -151,-151 -151,-227 0,-42 33,-75 75,-75 38,0 57,18 76,56z',
  stethoscope: 'M200 80c-30,0 -60,15 -70,45 -15,-30 -45,-45 -70,-45 -40,0 -70,30 -70,70 0,50 40,90 80,100 15,5 30,8 45,8 15,0 30,-3 45,-8 40,-10 80,-50 80,-100 0,-40 -30,-70 -70,-70z M120 220c-8,0 -15,7 -15,15 0,8 7,15 15,15 8,0 15,-7 15,-15 0,-8 -7,-15 -15,-15z M280 220c-8,0 -15,7 -15,15 0,8 7,15 15,15 8,0 15,-7 15,-15 0,-8 -7,-15 -15,-15z',
  plus: 'M200 100h-80v-80h-40v80h-80v40h80v80h40v-80h80v-40z'
};
const CONFETTI_COLORS = ['#1E5EFF', '#00BE52', '#6A5ACD', '#F59E0B'];

/**
 * ReviewsDisclaimer Component
 * Multi-step modal for submitting product reviews with clinical assessments
 */
const ReviewsDisclaimer = ({
  onClose,
  isVerified,
  submitReview,
  loading,
  error,
  productName,
  productImage
}) => {
  // The popup is reused for both product (`/products/[type]/[productID]`)
  // and pipeline (`/pipeline/[slug]`) detail pages. We pull whichever
  // param the current route exposes — they're mutually exclusive — and
  // pick the endpoint based on URL prefix below.
  const params = useParams();
  const pathname = usePathname();
  const isPipelineReview = (pathname || "").startsWith("/pipeline/");
  const productID = params?.productID || params?.slug;
  const user = useSelector(state => state?.userData?.userData?.user);

  // Agreement State
  const [checked, setChecked] = useState([false, false, false, false]);
  const allChecked = useMemo(() => checked.every(Boolean), [checked]);

  // Step Navigation
  const [step, setStep] = useState(1);

  // Form Data States
  const [selectedIndication, setSelectedIndication] = useState("");
  const [clinicalRatings, setClinicalRatings] = useState(INITIAL_CLINICAL_RATINGS);
  const [ratings, setRatings] = useState([]);
  const [review, setReview] = useState("");
  const [accessibilityScore, setAccessibilityScore] = useState(0);
  const [accessibilityCategory, setAccessibilityCategory] = useState("Very Poor");
  const [confidenceScore, setConfidenceScore] = useState(0);
  const [confidenceCategory, setConfidenceCategory] = useState("Unlikely");
  const [competitorComparison, setCompetitorComparison] = useState("");
  const [selectedCompetitor, setSelectedCompetitor] = useState("");
  const [showCompetitorInput, setShowCompetitorInput] = useState(false);
  const [clinicalInsight, setClinicalInsight] = useState("");
  const [insightFactors, setInsightFactors] = useState([]);
  const [otherFactorText, setOtherFactorText] = useState("");

  // Pipeline-specific state — only used when isPipelineReview is true.
  // Keeping the state local so the same modal component handles both
  // flows without prop drilling from the page.
  const [prescribeLikelihood, setPrescribeLikelihood] = useState(0);
  const [patientPopulation, setPatientPopulation] = useState("");
  // Pipeline review reuses the approved-flow `confidenceScore` /
  // `confidenceCategory` / `handleConfidenceChange` trio for clinical
  // confidence (declared further up in this component). Only one
  // review modal is mounted per page, so sharing state is safe and
  // avoids duplicating the same 0-100-slider component just to
  // rename props. `recommendToColleagues` (1-5) is unique to the
  // pipeline flow and lives below.
  const [recommendToColleagues, setRecommendToColleagues] = useState(0);
  // Single-select concern id (string). The step component coerces
  // legacy array values from older sessions to a scalar.
  const [biggestConcern, setBiggestConcern] = useState("");
  const [influencingFactor, setInfluencingFactor] = useState("");
  const [pipelineComment, setPipelineComment] = useState("");

  // ─── Pipeline RTK Query hooks ────────────────────────────────────
  // Hooks always run (Rules of Hooks), but `skip` prevents the actual
  // network/Mongo calls when this modal isn't being used for pipeline.
  // The submit mutation tuple destructures both the trigger and the
  // status — we only need the trigger here.
  const [submitPipelinePerspective] = useSubmitPipelinePerspectiveMutation();
  const {
    data: existingPipelinePerspective
  } = useGetMyPipelinePerspectiveQuery(productID, {
    skip: !isPipelineReview || !productID
  });
  const draftTargetType = isPipelineReview ? "pipeline" : "approved";
  const draftTargetKey = String(productID || "");
  const {
    data: reviewDraftData,
    isLoading: isReviewDraftLoading
  } = useReviewDraftQuery({
    targetType: draftTargetType,
    targetKey: draftTargetKey
  }, {
    skip: !draftTargetKey
  });
  const [saveReviewDraft] = useSaveReviewDraftMutation();
  const [deleteReviewDraft] = useDeleteReviewDraftMutation();
  const [draftReady, setDraftReady] = useState(false);
  const hasHydratedDraftRef = useRef(false);

  // Restore a draft once per modal mount. The saved step is clamped before
  // use so stale data can never jump into a submitted/terminal screen.
  useEffect(() => {
    if (!draftTargetKey || isReviewDraftLoading || hasHydratedDraftRef.current) {
      return;
    }
    const saved = reviewDraftData?.draft;
    const data = saved?.draft;
    if (saved && data && typeof data === "object") {
      const terminalStep = isPipelineReview ? 9 : 8;
      const savedStep = Math.min(terminalStep - 1, Math.max(2, Number(saved.step) || 2));
      if (Array.isArray(data.checked)) setChecked(data.checked.map(Boolean));
      setStep(savedStep);
      setSelectedIndication(data.selectedIndication || "");
      if (Array.isArray(data.clinicalRatings)) {
        setClinicalRatings(data.clinicalRatings);
      }
      if (Array.isArray(data.ratings)) setRatings(data.ratings);
      setAccessibilityScore(Number(data.accessibilityScore) || 0);
      setAccessibilityCategory(data.accessibilityCategory || "Very Poor");
      setConfidenceScore(Number(data.confidenceScore) || 0);
      setConfidenceCategory(data.confidenceCategory || "Unlikely");
      setCompetitorComparison(data.competitorComparison || "");
      setSelectedCompetitor(data.selectedCompetitor || "");
      setShowCompetitorInput(Boolean(data.showCompetitorInput));
      setClinicalInsight(data.clinicalInsight || "");
      setInsightFactors(Array.isArray(data.insightFactors) ? data.insightFactors : []);
      setOtherFactorText(data.otherFactorText || "");
      setPrescribeLikelihood(Number(data.prescribeLikelihood) || 0);
      setPatientPopulation(data.patientPopulation || "");
      setBiggestConcern(data.biggestConcern || "");
      setInfluencingFactor(data.influencingFactor || "");
      setRecommendToColleagues(Number(data.recommendToColleagues) || 0);
      setPipelineComment(data.pipelineComment || "");
    }
    hasHydratedDraftRef.current = true;
    setDraftReady(true);
  }, [draftTargetKey, isPipelineReview, isReviewDraftLoading, reviewDraftData?.draft]);

  // Pre-fill the pipeline form when the user has already submitted a
  // perspective for this drug. We populate ONLY when state is empty so
  // we never clobber in-progress edits. Runs once when the cached
  // response arrives (or when the user opens the modal on a drug they
  // already perspectived).
  useEffect(() => {
    if (!isPipelineReview) return;
    if (!draftReady || reviewDraftData?.draft) return;
    if (!existingPipelinePerspective) return;
    if (prescribeLikelihood > 0) return; // already started — don't clobber
    setPrescribeLikelihood(existingPipelinePerspective.prescribeLikelihood || 0);
    setPatientPopulation(existingPipelinePerspective.patientPopulation || "");
    // Coerce legacy array shape to a scalar so the single-select UI
    // pre-fills with the first stored concern.
    setBiggestConcern(Array.isArray(existingPipelinePerspective.biggestConcern) ? existingPipelinePerspective.biggestConcern[0] || "" : existingPipelinePerspective.biggestConcern || "");
    setInfluencingFactor(existingPipelinePerspective.influencingFactor || "");
    // Prefill confidence via the shared handler so the category
    // label (Unlikely / Rarely / Sometimes / Usually / Always) is
    // computed exactly the same way as a fresh user input — keeps
    // the chip in sync without duplicating the bucket logic.
    if (existingPipelinePerspective.clinicalConfidence != null) {
      handleConfidenceChange(existingPipelinePerspective.clinicalConfidence);
    }
    setRecommendToColleagues(existingPipelinePerspective.recommendToColleagues ?? 0);
    setPipelineComment(existingPipelinePerspective.comment || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftReady, existingPipelinePerspective, isPipelineReview, reviewDraftData?.draft]);

  // UI States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [openVerification, setOpenVerification] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Refs
  const hasShownConfettiRef = useRef(false);
  const portalContainerRef = useRef(null);
  const draftSnapshot = useMemo(() => ({
    checked,
    selectedIndication,
    clinicalRatings,
    ratings,
    accessibilityScore,
    accessibilityCategory,
    confidenceScore,
    confidenceCategory,
    competitorComparison,
    selectedCompetitor,
    showCompetitorInput,
    clinicalInsight,
    insightFactors,
    otherFactorText,
    prescribeLikelihood,
    patientPopulation,
    biggestConcern,
    influencingFactor,
    recommendToColleagues,
    pipelineComment
  }), [accessibilityCategory, accessibilityScore, biggestConcern, checked, clinicalInsight, clinicalRatings, competitorComparison, confidenceCategory, confidenceScore, influencingFactor, insightFactors, isPipelineReview, otherFactorText, patientPopulation, pipelineComment, prescribeLikelihood, ratings, recommendToColleagues, selectedCompetitor, selectedIndication, showCompetitorInput]);
  const productType = isPipelineReview ? "pipeline" : (pathname || "").split("/")[2] || "drug";
  const terminalStep = isPipelineReview ? 9 : 8;
  const draftCanBeSaved = draftReady && step >= 2 && step < terminalStep && Boolean(draftTargetKey);
  const persistReviewDraft = useCallback(() => {
    if (!draftCanBeSaved) return Promise.resolve();
    return saveReviewDraft({
      targetType: draftTargetType,
      targetKey: draftTargetKey,
      productName: productName || "Product",
      productType,
      productImage: productImage || "",
      href: pathname,
      step,
      draft: draftSnapshot
    }).unwrap().catch(saveError => {
      if (process.env.NODE_ENV === "development") {
        console.warn("Unable to autosave review draft:", saveError);
      }
    });
  }, [draftCanBeSaved, draftSnapshot, draftTargetKey, draftTargetType, pathname, productImage, productName, productType, saveReviewDraft, step]);

  // Debounce network writes while sliders/text inputs are changing. Closing
  // the modal also triggers the same save immediately below.
  useEffect(() => {
    if (!draftCanBeSaved) return undefined;
    const timer = window.setTimeout(persistReviewDraft, 600);
    return () => window.clearTimeout(timer);
  }, [draftCanBeSaved, persistReviewDraft]);
  const handleClose = useCallback(() => {
    void persistReviewDraft();
    onClose();
  }, [onClose, persistReviewDraft]);

  /**
   * Get step information by step number
   */
  const getStepInfo = useCallback(currentStep => {
    return STEP_INFO[currentStep] || {
      name: "",
      number: 0,
      total: TOTAL_STEPS
    };
  }, []);

  /**
   * Launch confetti celebration
   */
  const launchConfetti = useCallback(() => {
    if (hasShownConfettiRef.current) return;
    hasShownConfettiRef.current = true;
    const defaults = {
      scalar: 2,
      spread: 180,
      particleCount: 30,
      origin: {
        y: -0.1
      },
      startVelocity: -35,
      zIndex: CONFETTI_Z_INDEX
    };

    // Launch confetti with different shapes
    confetti({
      ...defaults,
      shapes: [confetti.shapeFromPath({
        path: CONFETTI_SHAPES.heart
      })],
      colors: [CONFETTI_COLORS[0], CONFETTI_COLORS[1], CONFETTI_COLORS[3]]
    });
    setTimeout(() => {
      confetti({
        ...defaults,
        shapes: [confetti.shapeFromPath({
          path: CONFETTI_SHAPES.stethoscope
        })],
        colors: [CONFETTI_COLORS[0], CONFETTI_COLORS[2], CONFETTI_COLORS[1]]
      });
    }, 200);
    setTimeout(() => {
      confetti({
        ...defaults,
        shapes: [confetti.shapeFromPath({
          path: CONFETTI_SHAPES.plus
        })],
        colors: [CONFETTI_COLORS[1], CONFETTI_COLORS[0], CONFETTI_COLORS[3]]
      });
    }, 400);
  }, []);

  /**
   * Toggle checkbox state
   */
  const toggleCheckbox = useCallback(index => {
    setChecked(prev => prev.map((item, i) => i === index ? !item : item));
  }, []);

  /**
   * Handle accessibility score change
   */
  const handleAccessibilityChange = useCallback(value => {
    setAccessibilityScore(value);
    if (value <= 20) {
      setAccessibilityCategory("Very Poor");
    } else if (value <= 40) {
      setAccessibilityCategory("Poor");
    } else if (value <= 60) {
      setAccessibilityCategory("Fair");
    } else if (value <= 80) {
      setAccessibilityCategory("Good");
    } else {
      setAccessibilityCategory("Excellent");
    }
  }, []);

  /**
   * Handle confidence score change
   */
  const handleConfidenceChange = useCallback(value => {
    setConfidenceScore(value);
    if (value <= 25) {
      setConfidenceCategory("Unlikely");
    } else if (value <= 50) {
      setConfidenceCategory("Rarely");
    } else if (value <= 75) {
      setConfidenceCategory("Sometimes");
    } else if (value < 100) {
      setConfidenceCategory("Usually");
    } else {
      setConfidenceCategory("Always");
    }
  }, []);

  /**
   * Reset all form states
   */
  const resetFormStates = useCallback(() => {
    // setSelectedIndication( "");
    setChecked([false, false, false, false]);
    setClinicalRatings(INITIAL_CLINICAL_RATINGS);
    setRatings([]);
    setReview("");
    setAccessibilityScore(0);
    setAccessibilityCategory("Very Poor");
    setConfidenceScore(0);
    setConfidenceCategory("Unlikely");
    setCompetitorComparison("");
    setSelectedCompetitor("");
    setShowCompetitorInput(false);
    setClinicalInsight("");
    setInsightFactors([]);
    setOtherFactorText("");
  }, []);

  /**
   * Step Navigation Handlers
   */
  const handleAgree = useCallback(() => {
    if (allChecked) {
      setStep(2);
    }
  }, [allChecked]);
  const handleIndicationNext = useCallback(() => {
    if (selectedIndication) {
      setStep(3);
    }
  }, [selectedIndication]);
  const handleClinicalPerformanceNext = useCallback(() => {
    const allRated = clinicalRatings.every(rating => rating.count > 0);
    if (allRated) {
      setStep(4);
    }
  }, [clinicalRatings]);
  const handleAccessibilityNext = useCallback(() => {
    setStep(5);
  }, []);
  const handleConfidenceScoreNext = useCallback(() => {
    setStep(6);
  }, []);
  const handleCompetitorComparisonNext = useCallback(() => {
    setStep(7);
  }, []);

  /**
   * Handle clinical insight submission
   */
  const handleClinicalInsightNext = useCallback(async () => {
    if (insightFactors.length === 0) return;
    const ratingsMap = {};
    clinicalRatings.forEach(r => {
      ratingsMap[r.tag] = r.count;
    });
    const finalReviewData = {
      // Pipeline reviews ship `pipelineSlug`; product reviews ship `product`.
      // The matching backend endpoint (selected in ReviewListing.submitReview)
      // reads the field that matches its discriminator.
      ...(isPipelineReview ? {
        pipelineSlug: productID
      } : {
        product: productID
      }),
      preference: 0,
      efficacy: ratingsMap.efficacy || 0,
      safety: ratingsMap.safety || 0,
      priceandaffordability: ratingsMap.priceandaffordability || 0,
      tolerability: ratingsMap.tolerability || 0,
      accessibility: accessibilityScore,
      adherence: ratingsMap.adherence || 0,
      review: clinicalInsight,
      insightFactors,
      otherFactorText,
      disclaimerAccepted: true,
      indication: selectedIndication,
      confidenceScore,
      competitorComparison,
      selectedCompetitor
    };
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const allRatings = [...clinicalRatings, ...ratings];
      await submitReview(allRatings, clinicalInsight, finalReviewData);
      await deleteReviewDraft({
        targetType: draftTargetType,
        targetKey: draftTargetKey
      }).unwrap().catch(() => undefined);
      launchConfetti();
      resetFormStates();
      setStep(8);
    } catch (err) {
      // `submitReview` (ReviewListing.jsx) already maps 401 / "Auth
      // failed" → friendly session-expired copy. This catch just
      // surfaces whatever message it threw. Belt-and-suspenders check
      // here too in case some other call path bubbles up the raw BE
      // string.
      const raw = err?.message || "";
      const isAuthError = raw === "Auth failed" || raw === "Authorization header missing";
      const friendly = isAuthError ? "Your session has expired. Please log out and log back in, then try again." : raw || "Failed to submit review. Please try again.";
      setSubmitError(friendly);
    } finally {
      setIsSubmitting(false);
    }
  }, [clinicalInsight, insightFactors, otherFactorText, clinicalRatings, ratings, productID, confidenceScore, accessibilityScore, selectedIndication, accessibilityCategory, confidenceCategory, competitorComparison, selectedCompetitor, deleteReviewDraft, draftTargetKey, draftTargetType, submitReview, launchConfetti, resetFormStates]);

  /**
   * Pipeline ("Share My Perspective") submit — UI-only stub.
   * Backend wiring lands in a follow-up; for now we log the payload
   * and advance to the "Submitted" terminal step so the flow is
   * end-to-end demoable for client sign-off.
   */
  const handlePipelineSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        slug: productID,
        // RTK Query trigger uses .slug
        prescribeLikelihood,
        patientPopulation,
        biggestConcern,
        influencingFactor,
        // New pipeline-review fields. Both are still optional on the
        // BE so older FE bundles continue to submit cleanly; sending
        // zero just means the doctor didn't move the slider, which
        // the BE treats as "no value" via the !=="" guard.
        // `confidenceScore` is shared with the approved-review flow.
        clinicalConfidence: confidenceScore,
        recommendToColleagues,
        comment: pipelineComment
      };
      // eslint-disable-next-line no-console
      console.log("[pipeline-perspective] submitting:", payload);
      const result = await submitPipelinePerspective(payload).unwrap();
      // eslint-disable-next-line no-console
      console.log("[pipeline-perspective] ✅ saved:", result);
      await deleteReviewDraft({
        targetType: draftTargetType,
        targetKey: draftTargetKey
      }).unwrap().catch(() => undefined);

      // Same celebratory confetti the approved-review flow shows on
      // success — fire-and-forget; the reusable launcher guards against
      // double-firing via its internal `hasShownConfettiRef`.
      launchConfetti();
      // Pipeline terminal is step 9 (bumped from 8 to make room for
      // the two new actionable steps inserted at 6 and 7).
      setStep(9);
    } catch (err) {
      // RTK Query errors come back as { status, data: { message } } from
      // fetchBaseQuery; native Errors come back with .message. Cover both
      // so the user sees the real reason instead of "[object Object]".
      const rawMsg = err?.data?.message || err?.error || err?.message || "Failed to submit perspective";
      // Translate JWT middleware errors (401 with "Auth failed" /
      // "Authorization header missing") into a friendly, action-
      // oriented message. The raw "Auth failed" was confusing doctors
      // mid-flow — they didn't know they'd been silently logged out.
      const isAuthError = err?.status === 401 || rawMsg === "Auth failed" || rawMsg === "Authorization header missing";
      const apiMsg = isAuthError ? "Your session has expired. Please log out and log back in, then try again." : rawMsg;
      // eslint-disable-next-line no-console
      console.error("[pipeline-perspective] ❌ submit failed:", err);
      setSubmitError(apiMsg);
    } finally {
      setIsSubmitting(false);
    }
  }, [productID, prescribeLikelihood, patientPopulation, biggestConcern, influencingFactor, confidenceScore, recommendToColleagues, pipelineComment, deleteReviewDraft, draftTargetKey, draftTargetType, launchConfetti, submitPipelinePerspective]);

  /**
   * Handle rating another indication
   */
  const handleRateAnotherIndication = useCallback(() => {
    setChecked([true, true, true, true]);
    setSelectedIndication("");
    setStep(2);
    setClinicalInsight("");
    setInsightFactors([]);
    setOtherFactorText("");
    setAccessibilityScore(0);
    setAccessibilityCategory("Very Poor");
    setConfidenceScore(0);
    setConfidenceCategory("Unlikely");
    setCompetitorComparison("");
    setSelectedCompetitor("");
    setShowCompetitorInput(false);
    setClinicalRatings(INITIAL_CLINICAL_RATINGS);
  }, []);

  /**
   * Progress Bar Component
   */
  const ProgressBar = useCallback(() => {
    // Approved flow has 6 actionable steps + a "Submitted" terminal step.
    // Pipeline flow has 5 actionable steps + the same terminal step.
    // Pipeline flow now: 7 actionable steps + 1 terminal (step 9).
    // Two new steps inserted between InfluencingFactor and OpenComment
    // — ClinicalConfidence (0-100 slider, parity with approved) and
    // RecommendToColleagues (1-5). OpenComment moved from step 6 → 8;
    // terminal moved from step 8 → 9 to make room.
    const steps = isPipelineReview ? [{
      step: 2,
      name: "Prescribing Intent",
      displayNumber: 0
    }, {
      step: 3,
      name: "Patient Population",
      displayNumber: 1
    }, {
      step: 4,
      name: "Biggest Concern",
      displayNumber: 2
    }, {
      step: 5,
      name: "Influencing Factor",
      displayNumber: 3
    }, {
      step: 6,
      name: "Clinical Confidence",
      displayNumber: 4
    }, {
      step: 7,
      name: "Recommend to Colleagues",
      displayNumber: 5
    }, {
      step: 8,
      name: "Open Comment",
      displayNumber: 6
    }, {
      step: 9,
      name: "Perspective Submitted",
      displayNumber: 7
    }] : [{
      step: 2,
      name: "Select Indication",
      displayNumber: 0
    }, {
      step: 3,
      name: "Clinical Performance",
      displayNumber: 1
    }, {
      step: 4,
      name: "Accessibility",
      displayNumber: 2
    }, {
      step: 5,
      name: "Clinical Confidence",
      displayNumber: 3
    }, {
      step: 6,
      name: "Competitor Comparison",
      displayNumber: 4
    }, {
      step: 7,
      name: "Real-World Insight",
      displayNumber: 5
    }, {
      step: 8,
      name: "Review Submitted",
      displayNumber: 6
    }];
    const total = isPipelineReview ? 7 : TOTAL_STEPS;
    const denominator = total + 1; // include terminal step in the % math

    const currentStepIndex = steps.findIndex(s => s.step === step);
    const currentStepInfo = steps[currentStepIndex] || steps[0];
    const progressPercent = Math.round((currentStepInfo.displayNumber + 1) / denominator * 100);
    return <>
        {/* Progress bar */}
        <div className="w-full bg-gray-200 h-2 overflow-hidden">
          <div className="bg-primary-primaryBlue h-2" style={{
          width: `${progressPercent}%`,
          transitionProperty: "width",
          transitionDuration: "1200ms",
          transitionTimingFunction: "cubic-bezier(0.25, 0.1, 0.25, 1)",
          transform: "translateZ(0)"
        }} />
        </div>

        {/* Step info */}
        <div className="flex items-center gap-2 px-4 sm:px-6 pt-4 sm:pt-7 pb-3 sm:pb-4">
          <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-primary-primaryBlue">
            <span className="text-[10px] sm:text-xs font-semibold text-white font-poppins">
              {progressPercent}% Complete
            </span>
          </div>
          <span className="text-xs sm:text-sm text-gray-400 font-poppins">
            Step {currentStepInfo.displayNumber} of {total}
          </span>
        </div>
      </>;
  }, [step, isPipelineReview]);

  /**
   * Review Header Component
   */
  const ReviewHeader = useCallback(() => {
    return <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-gray-200 flex-shrink-0 bg-white">
        <div className="flex justify-between items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3">
              <h1 className="flex gap-2 items-center text-base sm:text-xl font-semibold text-gray-900 font-poppins">
                <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                  <Image src={productImage || "/Images/placeholder_medicine.svg"} alt={productName || "Product"} width={48} height={48} className="w-full h-full object-contain" onError={e => {
                  e.target.src = "/Images/placeholder_medicine.svg";
                }} />
                </div>
                <span className="font-bold truncate">{productName || "Product"}</span>
                {selectedIndication && step > 2 && <span className="text-gray-600 text-xs sm:text-sm font-normal hidden sm:inline truncate">
                    - {selectedIndication}
                  </span>}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Terminal "Done" button — approved flow ends at step 8,
                pipeline flow ends at step 9. Both render
                ReviewSubmitted, so the close-button should appear in
                either case. */}
            {step === 8 || step === 9 ? <button onClick={onClose} className="px-3 sm:px-4 py-1.5 sm:py-2 text-primary-primaryBlue text-xs sm:text-sm font-semibold font-poppins rounded-lg hover:bg-primary-primaryBlue/10 transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-primaryBlue">
                Done
              </button> : <button onClick={handleClose} className="cursor-pointer hover:bg-gray-100 rounded-full p-1 transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-primaryBlue" aria-label="Close">
                <CrossIcon />
              </button>}
          </div>
        </div>
      </div>;
  }, [handleClose, onClose, productImage, productName, selectedIndication, step]);

  /**
   * Setup portal container
   */
  useEffect(() => {
    let container = document.getElementById(PORTAL_CONTAINER_ID);
    if (!container) {
      container = document.createElement("div");
      container.id = PORTAL_CONTAINER_ID;
      document.body.appendChild(container);
    }
    portalContainerRef.current = container;
    setMounted(true);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
      try {
        const containerToRemove = portalContainerRef.current;
        if (containerToRemove && document.body.contains(containerToRemove)) {
          document.body.removeChild(containerToRemove);
        }
        portalContainerRef.current = null;
      } catch (error) {
        // Silently handle cleanup errors
        if (process.env.NODE_ENV === "development") {
          console.warn("Portal container cleanup:", error);
        }
      }
    };
  }, []);
  if (!mounted || !portalContainerRef.current) return null;
  const modalContent = <>
      <style jsx global>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="fixed inset-0 popup-bg flex justify-center z-[99999] items-end sm:items-center">
        <div className="w-full sm:w-[95vw] md:w-[90vw] lg:w-[950px] max-w-[950px] h-full sm:h-auto sm:max-h-[90vh] z-[99999] bg-white rounded-none sm:rounded-xl shadow-2xl overflow-hidden flex flex-col min-h-0">
          {!isVerified ? <div className="flex flex-col h-full">
              {/* Header */}
              <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex justify-between items-center">
                  <h2 className="text-base sm:text-xl font-bold text-gray-900 font-poppins">
                    Verification Required
                  </h2>
                  <div onClick={handleClose} className="cursor-pointer hover:bg-gray-100 rounded-full p-1 transition-colors">
                    <CrossIcon />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-6 sm:py-8">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg mb-4 sm:mb-6">
                  <VerifyCheckIcon />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 font-poppins mb-2 text-center">
                  Get Verified to Write Reviews
                </h3>
                <p className="text-center text-gray-600 font-poppins max-w-sm text-sm sm:text-base">
                  You need to verify your credentials to write reviews and share your
                  clinical insights with the community.
                </p>
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex gap-2 sm:gap-3 flex-shrink-0 mt-auto bg-white">
                <button onClick={handleClose} className="flex-1 border-2 border-gray-300 text-gray-700 text-sm sm:text-base font-semibold font-poppins rounded-lg py-2.5 sm:py-3 hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button onClick={() => setOpenVerification(true)} className="flex-1 text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 py-2.5 sm:py-3 text-sm sm:text-base font-semibold font-poppins rounded-lg shadow-md transition-all duration-200 sm:transform sm:hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                  Get Verified
                </button>
              </div>
            </div> : <div className="flex flex-1 min-h-0 overflow-hidden">
              <div className="w-full flex flex-col flex-1 min-h-0 overflow-hidden bg-gray-50">
                <ReviewHeader />
                {step > 1 && <ProgressBar />}

                {step === 1 ? <Agreement checked={checked} toggleCheckbox={toggleCheckbox} allChecked={allChecked} onBack={handleClose} onNext={handleAgree} isPipeline={isPipelineReview} /> : isPipelineReview ?
            // ─── Pipeline "Share My Perspective" flow ──────────
            // 7 actionable steps (2-8) + terminal "submitted" (9).
            // Steps 6 (ClinicalConfidence) and 7 (RecommendToColleagues)
            // were inserted; OpenComment moved 6→8, terminal 8→9.
            step === 2 ? <PrescribeLikelihood prescribeLikelihood={prescribeLikelihood} setPrescribeLikelihood={setPrescribeLikelihood} productName={productName} onBack={() => setStep(1)} onNext={() => setStep(3)} /> : step === 3 ? <PatientPopulation patientPopulation={patientPopulation} setPatientPopulation={setPatientPopulation} productName={productName} onBack={() => setStep(2)} onNext={() => setStep(4)} /> : step === 4 ? <BiggestConcern biggestConcern={biggestConcern} setBiggestConcern={setBiggestConcern} productName={productName} onBack={() => setStep(3)} onNext={() => setStep(5)} /> : step === 5 ? <InfluencingFactor influencingFactor={influencingFactor} setInfluencingFactor={setInfluencingFactor} productName={productName} onBack={() => setStep(4)} onNext={() => setStep(6)} /> : step === 6 ?
            // Reuses the same `ClinicalConfidence` component
            // the approved-review flow uses — exact same UI
            // (slider, anchor labels, color treatment) and
            // the same state (`confidenceScore` +
            // `confidenceCategory` + `handleConfidenceChange`)
            // so doctors see identical behaviour on both
            // review surfaces.
            <ClinicalConfidence confidenceScore={confidenceScore} confidenceCategory={confidenceCategory} handleConfidenceChange={handleConfidenceChange} productName={productName} onBack={() => setStep(5)} onNext={() => setStep(7)} /> : step === 7 ? <RecommendToColleagues recommendToColleagues={recommendToColleagues} setRecommendToColleagues={setRecommendToColleagues} productName={productName} onBack={() => setStep(6)} onNext={() => setStep(8)} /> : step === 8 ? <OpenComment comment={pipelineComment} setComment={setPipelineComment} productName={productName} onBack={() => setStep(7)} onNext={handlePipelineSubmit} isSubmitting={isSubmitting} submitError={submitError} /> : step === 9 ? <ReviewSubmitted productName={productName} selectedIndication={selectedIndication}
            // Close, don't reload: the list behind the modal already
            // shows the review, and a reload could still be served the
            // page cached before it existed.
            onViewProductPage={handleClose} onRateAnotherIndication={handleRateAnotherIndication} isPipeline /> : null :
            // ─── Approved-product clinical-review flow ─────────
            step === 2 ? <IndicationSelection selectedIndication={selectedIndication} setSelectedIndication={setSelectedIndication} onBack={() => setStep(1)} onNext={handleIndicationNext} productID={productID} /> : step === 3 ? <ClinicalPerformance clinicalRatings={clinicalRatings} setClinicalRatings={setClinicalRatings} productName={productName} selectedIndication={selectedIndication} onBack={() => setStep(2)} onNext={handleClinicalPerformanceNext} /> : step === 4 ? <Accessibility accessibilityScore={accessibilityScore} accessibilityCategory={accessibilityCategory} handleAccessibilityChange={handleAccessibilityChange} productName={productName} onBack={() => setStep(3)} onNext={handleAccessibilityNext} /> : step === 5 ? <ClinicalConfidence confidenceScore={confidenceScore} confidenceCategory={confidenceCategory} handleConfidenceChange={handleConfidenceChange} productName={productName} onBack={() => setStep(4)} onNext={handleConfidenceScoreNext} /> : step === 6 ? <CompetitorComparison competitorComparison={competitorComparison} setCompetitorComparison={setCompetitorComparison} showCompetitorInput={showCompetitorInput} setShowCompetitorInput={setShowCompetitorInput} selectedCompetitor={selectedCompetitor} setSelectedCompetitor={setSelectedCompetitor} selectedIndication={selectedIndication} productName={productName} onBack={() => setStep(5)} onNext={handleCompetitorComparisonNext} /> : step === 7 ? <RealWorldInsight clinicalInsight={clinicalInsight} setClinicalInsight={setClinicalInsight} selectedFactors={insightFactors} setSelectedFactors={setInsightFactors} otherText={otherFactorText} setOtherText={setOtherFactorText} productName={productName} selectedIndication={selectedIndication} onBack={() => setStep(6)} onNext={handleClinicalInsightNext} isSubmitting={isSubmitting} submitError={submitError} /> : step === 8 ? <ReviewSubmitted productName={productName} selectedIndication={selectedIndication}
            // Close, don't reload: the list behind the modal already
            // shows the review, and a reload could still be served the
            // page cached before it existed.
            onViewProductPage={handleClose} onRateAnotherIndication={handleRateAnotherIndication} /> : null}
              </div>
            </div>}
        </div>
      </div>
    </>;
  return <>
      {portalContainerRef.current && createPortal(modalContent, portalContainerRef.current)}
      {openVerification && <NpiVerification closeModal={() => setOpenVerification(false)} />}
    </>;
};
ReviewsDisclaimer.propTypes = {
  onClose: PropTypes.func.isRequired,
  isVerified: PropTypes.bool,
  submitReview: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  productName: PropTypes.string,
  indication: PropTypes.string,
  productImage: PropTypes.string
};
ReviewsDisclaimer.defaultProps = {
  isVerified: false,
  loading: false,
  error: "",
  productName: "",
  indication: "",
  productImage: ""
};
export default ReviewsDisclaimer;